import {I18n} from 'aws-amplify/utils';
import {
    Drawer,
    DrawerProps,
    ScrollArea,
} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';
import {CreateDnsZoneInput} from '../types';

// FNP-1191 CRUD half — the drawer wrapper, per docs/patterns/drawer-pattern.md.
//
// ⚠️ THE TITLE IS FIXED AT "Add Zone" — there is no edit mode.
//
// The pattern doc derives the title from `record?.id` because its modules have
// both an add and an edit path through one drawer. This module has ONLY add:
// the neutral schema exposes no `updateDnsZone` (verified against the deployed
// TEST API), so an edit branch here would be unreachable code implying a
// capability that does not exist. Kept as a deliberate deviation rather than a
// dynamic title that is always the same value.
//
// The drawer geometry follows the doc exactly: right position, slide-up at
// 750ms, size xl, autosize scroll area for a form that outgrows the viewport.

export const FormDrawer = (
    {
        record,
        handleSubmit,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        record?: CreateDnsZoneInput,
        // ⚠️ RETURNS A PROMISE, not void. A `void` signature would let the
        // await below resolve immediately on a still-pending mutation, and the
        // drawer would close before the create had either succeeded or failed.
        handleSubmit: (values: CreateDnsZoneInput) => Promise<unknown>,
    },
) => {

    // Submit, THEN close — the doc's handleFormSubmit shape, and the ordering
    // is the whole point.
    //
    // ⚠️ A REJECTED SUBMIT MUST NOT REACH onClose(). The await is what
    // guarantees it: if handleSubmit rejects, this function rejects with it and
    // the drawer stays open, holding the operator's input so they can correct
    // it. Closing on failure would discard a filled-in form and leave only a
    // toast to explain a mistake they can no longer see.
    //
    // The rejection is deliberately NOT caught. It has already been classified
    // and notified by the mutation hook's onError, and Formik treats the
    // rejected submit as a finished (failed) submission, which is exactly the
    // state the form should be in.
    const handleFormSubmit = async (values: CreateDnsZoneInput) => {
        await handleSubmit(values);
        onClose();
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Add Zone')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <RecordForm
                record={record}
                handleSubmit={handleFormSubmit}
                handleCancel={onClose}
            />
        </Drawer>
    );
};
